import React, { useState, useCallback } from 'react';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowDown, ArrowUp, Plus, Edit2, Trash2, ChevronLeft,
  Save, X, Phone, AlertCircle, CheckCircle, ToggleLeft, ToggleRight
} from 'lucide-react';

const STRIP_OPTIONS = [
  { value: 0, label: 'No eliminar dígitos' },
  { value: 1, label: 'Quitar el primer 1 dígito' },
  { value: 2, label: 'Quitar el(los) primer(os) 2 dígitos' },
  { value: 3, label: 'Quitar el(los) primer(os) 3 dígitos' },
  { value: 4, label: 'Quitar el(los) primer(os) 4 dígitos' },
  { value: 5, label: 'Quitar el(los) primer(os) 5 dígitos' },
  { value: 6, label: 'Quitar el(los) primer(os) 6 dígitos' },
];

const EMPTY_ROUTE_TRUNK = { trunkId: '', stripDigits: 0, prepend: '', callerId: '' };

function emptyOutbound() {
  return {
    name: '',
    prefixFilter: '',
    extensionFilter: '',
    lengthMin: '',
    lengthMax: '',
    priority: 0,
    emergencyRoute: false,
    isActive: true,
    routeTrunks: [
      { ...EMPTY_ROUTE_TRUNK },
      { ...EMPTY_ROUTE_TRUNK },
      { ...EMPTY_ROUTE_TRUNK },
      { ...EMPTY_ROUTE_TRUNK },
    ],
  };
}

// ── Outbound Route Editor ──────────────────────────────────────────────────────
function OutboundEditor({ route, trunks, onSave, onCancel }) {
  const [form, setForm] = useState(() => {
    if (!route) return emptyOutbound();
    const rts = Array.from({ length: 4 }, (_, i) =>
      route.route_trunks?.[i]
        ? { trunkId: route.route_trunks[i].trunk_id || '', stripDigits: route.route_trunks[i].strip_digits || 0, prepend: route.route_trunks[i].prepend || '', callerId: route.route_trunks[i].caller_id_override || '' }
        : { ...EMPTY_ROUTE_TRUNK }
    );
    return {
      name: route.name || '',
      prefixFilter: route.prefix_filter || '',
      extensionFilter: route.extension_filter || '',
      lengthMin: route.length_min || '',
      lengthMax: route.length_max || '',
      priority: route.priority || 0,
      emergencyRoute: route.emergency_route || false,
      isActive: route.is_active !== false,
      routeTrunks: rts,
    };
  });

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setTrunk = (i, k, v) => setForm(f => {
    const rts = [...f.routeTrunks];
    rts[i] = { ...rts[i], [k]: v };
    return { ...f, routeTrunks: rts };
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    const payload = {
      ...form,
      lengthMin: form.lengthMin ? parseInt(form.lengthMin) : 0,
      lengthMax: form.lengthMax ? parseInt(form.lengthMax) : 0,
      routeTrunks: form.routeTrunks.filter(rt => rt.trunkId),
    };
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button type="button" onClick={onCancel} aria-label="Volver"
          className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">
          <ChevronLeft size={20} />
        </button>
        <div>
          <h2 className="text-xl font-bold text-white">
            {route ? `Editar: ${route.name}` : 'Nueva Regla de Salida'}
          </h2>
        </div>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={onCancel}
            className="btn-secondary flex items-center gap-2">
            <X size={16} /> Cancelar
          </button>
          <button type="submit"
            className="btn-primary flex items-center gap-2">
            <Save size={16} /> Guardar
          </button>
        </div>
      </div>

      {/* Nombre */}
      <div className="card space-y-4">
        <div>
          <label className="form-label" htmlFor="route-name">Nombre de la regla <span className="text-red-400">*</span></label>
          <input id="route-name" className="form-input" value={form.name}
            onChange={e => setField('name', e.target.value)}
            placeholder="Ej: Salida Nacional, Internacional, Emergencias..." />
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label" htmlFor="route-prefix-filter">Llamadas a números que comienzan con el prefijo</label>
            <input id="route-prefix-filter" className="form-input" value={form.prefixFilter}
              onChange={e => setField('prefixFilter', e.target.value)}
              placeholder="Ej: 0-3,5,6. Vacío = cualquier prefijo" />
            <p className="text-xs text-muted mt-1">Ej: 0-3,5,6. Dejar vacío para cualquier prefijo</p>
          </div>
          <div>
            <label className="form-label" htmlFor="route-extension-filter">Llamadas desde la(s) extensión(es)</label>
            <input id="route-extension-filter" className="form-input" value={form.extensionFilter}
              onChange={e => setField('extensionFilter', e.target.value)}
              placeholder="Ej: 27210,27211-27220" />
            <p className="text-xs text-muted mt-1">Ej: 10000,10003-10005. Vacío = cualquier extensión</p>
          </div>
          <div>
            {/* span, no label: agrupa DOS inputs (mín/máx), cada uno lleva su propio aria-label */}
            <span className="form-label">Llamadas a números con una longitud de</span>
            <div className="flex gap-2 items-center">
              <input className="form-input" type="number" min="0" value={form.lengthMin}
                aria-label="Longitud mínima"
                onChange={e => setField('lengthMin', e.target.value)}
                placeholder="Mín" />
              <span className="text-muted text-sm">a</span>
              <input className="form-input" type="number" min="0" value={form.lengthMax}
                aria-label="Longitud máxima"
                onChange={e => setField('lengthMax', e.target.value)}
                placeholder="Máx" />
            </div>
            <p className="text-xs text-muted mt-1">Ej: 9,10,13-15. Vacío = cualquier longitud</p>
          </div>
        </div>
      </div>

      {/* Rutas de troncal con failover */}
      <div className="card">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-white">Realizar llamadas salientes en</h3>
          <p className="text-sm text-muted mt-1">
            Configure una ruta principal y hasta 3 rutas de respaldo en caso de que la principal falle.
            Debe configurarse al menos una ruta para que la regla funcione.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="table-th w-20"></th>
                <th className="table-th">Troncal</th>
                <th className="table-th">Remover dígitos</th>
                <th className="table-th w-36">Anteponer</th>
                <th className="table-th">Identificador de Llamada Saliente</th>
              </tr>
            </thead>
            <tbody>
              {form.routeTrunks.map((rt, i) => (
                <tr key={i} className="border-b border-gray-800/50">
                  <td className="table-td">
                    <span className="text-sm font-medium text-gray-400">Ruta {i + 1}</span>
                  </td>
                  <td className="table-td py-2">
                    <div className="flex items-center gap-2">
                      <select className="form-input text-sm" value={rt.trunkId}
                        aria-label={`Troncal para la ruta ${i + 1}`}
                        onChange={e => setTrunk(i, 'trunkId', e.target.value)}>
                        <option value="">Seleccionar troncal</option>
                        {trunks.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      {rt.trunkId && (
                        <button type="button" onClick={() => setTrunk(i, 'trunkId', '')}
                          aria-label={`Quitar troncal de la ruta ${i + 1}`}
                          className="text-muted hover:text-red-400 flex-shrink-0">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="table-td py-2">
                    <select className="form-input text-sm" value={rt.stripDigits}
                      aria-label={`Remover dígitos para la ruta ${i + 1}`}
                      onChange={e => setTrunk(i, 'stripDigits', parseInt(e.target.value))}>
                      {STRIP_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="table-td py-2">
                    <input className="form-input text-sm" value={rt.prepend}
                      aria-label={`Anteponer dígitos para la ruta ${i + 1}`}
                      onChange={e => setTrunk(i, 'prepend', e.target.value)}
                      placeholder="Ej: 00, 34..." />
                  </td>
                  <td className="table-td py-2">
                    <input className="form-input text-sm" value={rt.callerId}
                      aria-label={`Identificador de llamada saliente para la ruta ${i + 1}`}
                      onChange={e => setTrunk(i, 'callerId', e.target.value)}
                      placeholder="Ej: +34912345678" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Opciones adicionales */}
      <div className="card">
        <h3 className="text-base font-semibold text-white mb-4">Opciones adicionales</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label" htmlFor="route-priority">Prioridad</label>
            <input id="route-priority" className="form-input" type="number" value={form.priority}
              onChange={e => setField('priority', parseInt(e.target.value) || 0)} />
            <p className="text-xs text-muted mt-1">Mayor número = mayor prioridad</p>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <button type="button" onClick={() => setField('emergencyRoute', !form.emergencyRoute)}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border transition-colors ${
                form.emergencyRoute
                  ? 'border-red-700 bg-red-900/20 text-red-400'
                  : 'border-gray-700 text-gray-400 hover:border-gray-600'
              }`}>
              <AlertCircle size={16} />
              {form.emergencyRoute ? 'Ruta de emergencia: Sí' : 'Ruta de emergencia: No'}
            </button>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <button type="button" onClick={() => setField('isActive', !form.isActive)}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border transition-colors ${
                form.isActive
                  ? 'border-green-700 bg-green-900/20 text-green-400'
                  : 'border-gray-700 text-gray-400 hover:border-gray-600'
              }`}>
              {form.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              {form.isActive ? 'Activa' : 'Inactiva'}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

// ── Outbound List ──────────────────────────────────────────────────────────────
function OutboundList({ routes, trunks, onEdit, onCreate, onDelete, onToggle }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Rutas Salientes</h2>
          <p className="text-sm text-muted">{routes.length} regla{routes.length !== 1 ? 's' : ''} configurada{routes.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={onCreate} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Nueva Regla
        </button>
      </div>

      {!routes.length ? (
        <div className="card text-center py-16">
          <Phone size={40} className="text-gray-700 mx-auto mb-3" />
          <p className="text-muted font-medium">Sin reglas de salida configuradas</p>
          <p className="text-muted text-sm mt-1">Crea una regla para enrutar las llamadas salientes</p>
          <button onClick={onCreate} className="btn-primary mt-4 inline-flex items-center gap-2">
            <Plus size={16} /> Crear primera regla
          </button>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th className="table-th">Nombre</th>
                <th className="table-th">Prefijo</th>
                <th className="table-th">Troncales (rutas)</th>
                <th className="table-th">Prioridad</th>
                <th className="table-th">Estado</th>
                <th className="table-th text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r, idx) => {
                const activeTrunks = (r.route_trunks || []).filter(rt => rt.trunk_id);
                const trunkNames = activeTrunks.map(rt => {
                  const t = trunks.find(t => t.id === rt.trunk_id);
                  return t?.name || rt.trunk_id;
                });
                return (
                  <tr key={r.id} className={`table-row border-b border-gray-800/50 ${idx % 2 === 0 ? '' : 'bg-gray-900/20'}`}>
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        {r.emergency_route && (
                          <span className="badge-danger text-xs">Emergencia</span>
                        )}
                        <span className="font-medium text-gray-100">{r.name}</span>
                      </div>
                    </td>
                    <td className="table-td">
                      {r.prefix_filter
                        ? <span className="font-mono text-blue-400 text-sm">{r.prefix_filter}</span>
                        : <span className="text-muted text-sm">Cualquier prefijo</span>
                      }
                    </td>
                    <td className="table-td">
                      {trunkNames.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {trunkNames.map((name, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-blue-900/30 text-blue-300 border border-blue-800/50 rounded">
                              {i + 1}. {name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted text-sm">Sin troncal</span>
                      )}
                    </td>
                    <td className="table-td text-gray-400">{r.priority}</td>
                    <td className="table-td">
                      <button onClick={() => onToggle(r)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${
                          r.is_active
                            ? 'badge-success border-green-800 cursor-pointer hover:bg-green-900/40'
                            : 'badge-danger border-red-800 cursor-pointer hover:bg-red-900/40'
                        }`}>
                        {r.is_active ? 'Activa' : 'Inactiva'}
                      </button>
                    </td>
                    <td className="table-td text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => onEdit(r)}
                          className="p-1.5 text-muted hover:text-blue-400 hover:bg-blue-900/20 rounded transition-colors"
                          title="Editar">
                          <Edit2 size={15} />
                        </button>
                        <button onClick={() => onDelete(r)}
                          className="p-1.5 text-muted hover:text-red-400 hover:bg-red-900/20 rounded transition-colors"
                          title="Eliminar">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Inbound List ───────────────────────────────────────────────────────────────
function InboundList({ routes }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Rutas Entrantes</h2>
          <p className="text-sm text-muted">{routes.length} ruta{routes.length !== 1 ? 's' : ''} configurada{routes.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-900/50">
              {['DID / Número', 'Troncal', 'Destino', 'Tipo', 'Prioridad', 'Estado'].map(h => (
                <th key={h} className="table-th">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {routes.map(r => (
              <tr key={r.id} className="table-row border-b border-gray-800/50">
                <td className="table-td font-mono text-blue-400">{r.did || 'Cualquier DID'}</td>
                <td className="table-td text-sm text-gray-400">{r.trunk_name || 'Todas'}</td>
                <td className="table-td font-mono text-sm">{r.destination}</td>
                <td className="table-td"><span className="badge-info text-xs">{r.destination_type}</span></td>
                <td className="table-td text-gray-400">{r.priority}</td>
                <td className="table-td">
                  <span className={r.is_active ? 'badge-success' : 'badge-danger'}>
                    {r.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </td>
              </tr>
            ))}
            {!routes.length && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-muted">
                  Sin rutas entrantes configuradas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function DialplanPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('outbound');
  const [editRoute, setEditRoute] = useState(null); // null=list, false=new, object=edit
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  useEscapeKey(!!deleteConfirm, () => setDeleteConfirm(null));

  const { data: inbound = [] } = useQuery('inbound-routes',
    () => api.get('/dialplan/inbound-routes').then(r => r.data));
  const { data: outbound = [], isLoading: loadingOut } = useQuery('outbound-routes',
    () => api.get('/dialplan/outbound-routes').then(r => r.data));
  const { data: trunks = [] } = useQuery('trunks-active',
    () => api.get('/trunks').then(r => r.data.filter(t => t.status === 'active')));

  const saveMutation = useMutation(
    (data) => {
      if (editRoute && editRoute.id)
        return api.put(`/dialplan/outbound-routes/${editRoute.id}`, data).then(r => r.data);
      return api.post('/dialplan/outbound-routes', data).then(r => r.data);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('outbound-routes');
        toast.success(editRoute?.id ? 'Ruta actualizada' : 'Ruta creada');
        setEditRoute(null);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Error al guardar'),
    }
  );

  const toggleMutation = useMutation(
    (r) => api.put(`/dialplan/outbound-routes/${r.id}`, { isActive: !r.is_active }).then(res => res.data),
    {
      onSuccess: () => qc.invalidateQueries('outbound-routes'),
      onError: () => toast.error('Error al cambiar estado'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/dialplan/outbound-routes/${id}`).then(r => r.data),
    {
      onSuccess: () => { qc.invalidateQueries('outbound-routes'); toast.success('Ruta eliminada'); setDeleteConfirm(null); },
      onError: () => toast.error('Error al eliminar'),
    }
  );

  const isEditing = editRoute !== null;

  return (
    <div className="space-y-6">
      {!isEditing && (
        <>
          <div>
            <h1 className="text-2xl font-bold text-white">Plan de Marcado</h1>
            <p className="text-gray-400 text-sm">Configure rutas entrantes y salientes</p>
          </div>

          <div className="flex gap-2 border-b border-gray-800">
            {[
              { key: 'outbound', icon: ArrowUp, label: 'Reglas Salientes', count: outbound.length },
              { key: 'inbound', icon: ArrowDown, label: 'Rutas Entrantes', count: inbound.length },
            ].map(({ key, icon: Icon, label, count }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === key
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-muted hover:text-gray-300'
                }`}>
                <Icon size={15} />
                {label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  tab === key ? 'bg-blue-900/50 text-blue-300' : 'bg-gray-800 text-muted'
                }`}>{count}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Tab content */}
      {!isEditing && tab === 'outbound' && (
        <OutboundList
          routes={outbound}
          trunks={trunks}
          onEdit={(r) => setEditRoute(r)}
          onCreate={() => setEditRoute(false)}
          onDelete={(r) => setDeleteConfirm(r)}
          onToggle={(r) => toggleMutation.mutate(r)}
        />
      )}

      {!isEditing && tab === 'inbound' && (
        <InboundList routes={inbound} />
      )}

      {isEditing && (
        <OutboundEditor
          route={editRoute || null}
          trunks={trunks}
          onSave={(data) => saveMutation.mutate(data)}
          onCancel={() => setEditRoute(null)}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div role="dialog" aria-modal="true" className="card max-w-md w-full space-y-4">
            <h3 className="text-lg font-semibold text-white">Eliminar ruta</h3>
            <p className="text-gray-400">
              ¿Eliminar la regla <span className="text-white font-medium">"{deleteConfirm.name}"</span>?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancelar</button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isLoading}
                className="btn-danger flex items-center gap-2">
                <Trash2 size={15} /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
